import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import {
  useGetRecommendedAccountsQuery,
  useGetRecommendedFiltersQuery,
} from "../api";
import { useEffect, useMemo, useState } from "react";
import { ResetAllFiltersButton } from "../components/features/ResetAllFiltersButton";
import { InputWithQuery } from "../components/ui/InputWithQuery";
import {
  useAppDispatch,
  useAppSelector,
  useHandleError,
  useStateWithSearchParams,
} from "../hooks";
import { QUERY_SEPARATOR } from "../constant";
import { TableTabs } from "../components/ui";
import { DropdownWithQuery } from "../components/ui/dropdown/DropdownWithQuery";
import {
  RecommendedActionBlock,
  RecommendedTableBlock,
} from "../components/recommended";
import { RecommendedAccount } from "../types/apiTypes";
import { TierType } from "../types/enums";
import { TierMapper } from "../types/genereal";
import { prepareFilters } from "../utils/prepareFilters";
import { DropdownWithAccordion } from "../components/ui/dropdown";
import { SelectedFilterList } from "../components/features/SelectedFilterList";
import { AddToTargetedListBtn } from "../components/recommended/AddToTargetedListBtn";
import { setRemovedIds } from "../features/companySlice";
import {
  ExportRecommendedBtn,
  ReuseGoToTopBtn,
} from "../components/ui/buttons";
import { RecommendedGridBlock } from "../components/recommended/RecommendedGridBlock";

type SearchParams = {
  sizes?: string;
  industries?: string;
  regions?: string;
  search?: string;
  sort?: string;
  selected?: string;
  tiers?: string;
  revenue?: string;
};

enum ColumnType {
  grid = "grid",
  row = "row",
}
const statusFilterList = [
  "All Tiers",
  TierMapper[TierType.high].title,
  TierMapper[TierType.medium].title,
  TierMapper[TierType.low].title,
];

const RecommendedPage = () => {
  const { campaign_id } = useParams();
  const dispatch = useAppDispatch();
  const removedAccounts = useAppSelector((state) => state.company.removedIDs);

  const [recommendedAccounts, setRecommendedAccounts] = useState<
    RecommendedAccount[]
  >([]);
  const [columnsType, setColumnsType] = useState<ColumnType>(ColumnType.row);

  const { searchDict, setSearchValue } = useStateWithSearchParams<SearchParams>(
    {
      industries: undefined,
      regions: undefined,
      sizes: undefined,
      search: undefined,
      sort: undefined,
      selected: undefined,
      tiers: undefined,
      revenue: undefined,
    },
  );

  const { data: filters } = useGetRecommendedFiltersQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  const {
    data: recommended,
    error,
    isLoading,
    isFetching,
  } = useGetRecommendedAccountsQuery(
    {
      campaign_id: campaign_id!,
    },
    { skip: !campaign_id, refetchOnMountOrArgChange: true },
  );

  useHandleError(error);

  useEffect(() => {
    recommended && setRecommendedAccounts(recommended.items);
  }, [recommended]);

  useEffect(() => {
    if (removedAccounts.length > 0) {
      setRecommendedAccounts((prev) =>
        prev.filter((acc) => !removedAccounts.includes(acc.id)),
      );
    }
  }, [removedAccounts]);

  const removeFilterHandler = (key: string, filter: string) => {
    const filters = searchDict[key as keyof SearchParams];
    if (!filters) return;

    setSearchValue(
      key as keyof SearchParams,
      filters
        .split(QUERY_SEPARATOR)
        .filter((el) => el !== filter)
        .join(QUERY_SEPARATOR),
    );
  };

  const preparedFilters = prepareFilters(filters);

  const isFilters = preparedFilters.some(
    ({ query }) => searchDict[query as keyof SearchParams],
  );

  const isGrid = ColumnType.grid === columnsType;

  const filteredData = useMemo(() => {
    const data: Record<TierType, RecommendedAccount[]> = {
      [TierType.high]: [],
      [TierType.medium]: [],
      [TierType.low]: [],
    };

    recommendedAccounts.forEach((account) => {
      if (
        searchDict.search &&
        !account.name.toLowerCase().includes(searchDict.search.toLowerCase())
      )
        return;

      if (
        searchDict.tiers &&
        !searchDict.tiers
          .split(QUERY_SEPARATOR)
          .includes(TierMapper[account.tier].title)
      )
        return;

      if (
        searchDict.regions &&
        !searchDict.regions.split(QUERY_SEPARATOR).includes(account.region)
      )
        return;

      if (
        searchDict.industries &&
        !searchDict.industries.split(QUERY_SEPARATOR).includes(account.industry)
      )
        return;

      if (
        searchDict.sizes &&
        !searchDict.sizes.split(QUERY_SEPARATOR).includes(account.size)
      )
        return;

      if (
        searchDict.revenue &&
        !searchDict.revenue.split(QUERY_SEPARATOR).includes(account.revenue)
      )
        return;

      data[account.tier].push(account);
    });

    return data;
  }, [recommendedAccounts, searchDict]);

  const removeHandler = (ids: string[]) => {
    dispatch(setRemovedIds(ids));
    setSearchValue("selected", undefined);
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={"8vh"}
      >
        <CircularProgress size={30} sx={{ color: "#FE8836" }} />
      </Box>
    );
  }

  const isDemo = recommendedAccounts.length === 0;

  return (
    <Stack gap="24px">
      <RecommendedActionBlock
        campaign_id={campaign_id!}
        isFirstSearch={isDemo}
      />

      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          gap: "12px",
          justifyContent: "space-between",
        }}
      >
        <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "12px" }}>
          <TableTabs isGrid={isGrid} setColumnsType={setColumnsType} />
          <InputWithQuery isDisabled={isDemo} />
          <Stack>
            <DropdownWithQuery
              isDisabled={isDemo}
              query={"tiers"}
              label={""}
              items={statusFilterList}
              placeholder={""}
              initSelected={statusFilterList[0]}
              maxHeight="134px"
              maxItemsWidth={"172px"}
              selectedAllOption={statusFilterList[0]}
            />
          </Stack>

          <Stack flexDirection={"row"} gap="12px">
            {prepareFilters(filters).length > 0 && (
              <DropdownWithAccordion
                isActive={preparedFilters.some(
                  ({ query }) => searchDict[query as keyof SearchParams],
                )}
                data={preparedFilters}
                selected={preparedFilters.reduce(
                  (acc, { query }) =>
                    acc +
                    (searchDict[query as keyof SearchParams]?.split(
                      QUERY_SEPARATOR,
                    )?.length || 0),
                  0,
                )}
              />
            )}
            {isFilters && <ResetAllFiltersButton />}
          </Stack>
        </Stack>
        <Stack sx={{ flexDirection: "row", alignItems: "center", gap: "12px" }}>
          <ExportRecommendedBtn isDisabled={isDemo} />

          {!isGrid && (
            <AddToTargetedListBtn
              ids={searchDict.selected?.split(QUERY_SEPARATOR) || []}
              successHandler={removeHandler}
            />
          )}
        </Stack>
      </Stack>

      {isFilters && (
        <Stack
          direction="row"
          justifyContent="space-between"
          gap="16px"
          alignItems="flex-end"
        >
          <Stack direction="row" flexWrap="wrap" gap="8px">
            {preparedFilters.map(({ placeholder, query }) => (
              <SelectedFilterList
                key={query}
                filters={
                  searchDict[query as keyof SearchParams]?.split(
                    QUERY_SEPARATOR,
                  ) || []
                }
                label={placeholder}
                removeHandler={removeFilterHandler}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {isFetching && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height={"8vh"}
        >
          <CircularProgress size={30} sx={{ color: "#FE8836" }} />
        </Box>
      )}

      {(isFilters || searchDict.search) &&
      !isFetching &&
      Object.values(filteredData).every((el) => el.length === 0) ? (
        <Stack
          gap="8px"
          alignItems="center"
          justifyContent="center"
          padding="32px"
        >
          <Typography
            variant="subtitle1"
            color="custom.textDarkBlue"
            textAlign="center"
            fontWeight={500}
          >
            No similar accounts discovered yet
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center">
            We haven’t found any recommended accounts based on your current
            targets.
            <br />
            Explore similar accounts to support account expansion.
          </Typography>
        </Stack>
      ) : isGrid ? (
        <RecommendedGridBlock recommendedAccounts={filteredData} />
      ) : (
        <RecommendedTableBlock recommendedAccounts={filteredData} />
      )}

      <ReuseGoToTopBtn element={document.body} />
    </Stack>
  );
};

export default RecommendedPage;
